import cx from 'classnames';
import dayjs from 'dayjs';
import { useCallback, useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import useDebounce from 'src/hooks/useDebounce';
import { Commission } from 'src/models/commission.model';
import { CreateTimesheetPayload } from 'src/models/timesheet.model';
import { useAppDispatch, useAppSelector } from 'src/reducers';
import { createUserTimesheet, deleteUserTimesheet, updateUserTimesheet } from 'src/reducers/user-timesheet-management.reducer';
import { getItalianHolidays } from 'src/utils/holidays-utils';
import styles from './timesheet-table.module.scss';

type Props = {
  commission: Commission;
  index: number;
  year: number;
  month: number;
};

export const TimesheetTableCell = (props: Props) => {
  const { commission, index, year, month } = props;
  const dispatch = useAppDispatch();
  const location = useLocation();
  const params = useParams();

  const userId = params.id;

  const [hours, setHours] = useState<number | null>(null);
  const [regexError, setRegexError] = useState(false);

  const debouncedHours = useDebounce(hours, 500);

  const { userTimesheets, loading } = useAppSelector(state => state.searchUserTimesheets);

  const day = dayjs()
    .month(month)
    .date(index + 1)
    .format('dddd');

  const holidays = getItalianHolidays(year, 'Rome');

  const isHoliday =
    holidays.some(holiday =>
      dayjs(holiday.date).isSame(
        dayjs()
          .year(year)
          .month(month)
          .date(index + 1),
        'day'
      )
    ) ||
    day === 'Saturday' ||
    day === 'Sunday';

  const timesheet =
    userTimesheets?.workedHours[
      `${dayjs()
        .year(year)
        .month(month)
        .date(index + 1)
        .format('YYYY-MM-DD')}-${commission?.id}`
    ];

  const date = dayjs()
    .year(year)
    .month(month)
    .date(index + 1)
    .format('YYYY-MM-DD');

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const regex = /^(2[0-4]|1[0-9]|[1-9])$/;

    if (!regex.test(value)) {
      setHours(null);
      setRegexError(true);
    }

    if (value === '' || regex.test(value)) {
      setHours(value === '' ? 0 : Number(value));
      setRegexError(false);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'e' || e.key === 'E') {
      e.preventDefault();
    }
  };

  const handleTimesheet = useCallback(() => {
    if (timesheet?.id) {
      const updatedTimesheet = {
        ...timesheet,
        hours: hours || 0,
        userId: Number(userId),
        commissionId: timesheet.commission.id,
      };
      const deletedTimesheet = {
        ...timesheet,
        hours: hours || 0,
        userId: Number(userId),
        commission: timesheet.commission,
      };

      if (hours === 0 || hours === null) {
        dispatch(deleteUserTimesheet(deletedTimesheet));
      } else {
        dispatch(updateUserTimesheet(updatedTimesheet));
      }
    } else {
      const newTimesheet: CreateTimesheetPayload = {
        referenceDate: date,
        hours: hours as number,
        commission: commission,
        userId: Number(userId),
      };

      dispatch(createUserTimesheet(newTimesheet));
    }
  }, [timesheet?.id, hours, dispatch, location?.state?.id, commission, date]);

  const classNames = cx(styles.timesheetCell, {
    [styles.holidayBackground]: isHoliday,
    [styles.timesheetCellError]: regexError,
  });

  useEffect(() => {
    if (debouncedHours !== null) {
      handleTimesheet();
    }
  }, [debouncedHours]);

  return (
    <td className={classNames}>
      <input
        type="number"
        className={styles.timesheetInput}
        onChange={onInputChange}
        onKeyDown={onKeyDown}
        disabled={isHoliday || loading}
        defaultValue={timesheet?.hours && timesheet?.hours > 0 ? timesheet.hours : ''}
      />
    </td>
  );
};
